import { CSSProperties, Fragment, useState } from 'react';
import { useAppStore } from '../state/store';

/**
 * Session view — an Ableton-style clip launch grid. Rows are scenes,
 * columns are channels, and each cell holds a pattern.
 *
 * - Click a cell: launch (or stop) that clip.
 * - Double-click a cell: pick which pattern it holds.
 * - ▶ at the end of a row: launch (or stop) the whole scene.
 */
export function SessionView() {
  const project = useAppStore((s) => s.project);
  const sessionActive = useAppStore((s) => s.sessionActive);
  const sessionActiveScene = useAppStore((s) => s.sessionActiveScene);
  const {
    sceneToggle,
    cellToggle,
    addScene,
    removeScene,
    renameScene,
    setSceneCell,
    createPattern,
  } = useAppStore.getState();

  const [picker, setPicker] = useState<{ sceneId: string; channelId: string } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const channels = project.channels;
  const pickerScene = picker ? project.scenes.find((s) => s.id === picker.sceneId) : null;
  const pickerChannel = picker ? channels.find((c) => c.id === picker.channelId) : null;

  return (
    <div className="session" onPointerDown={() => setPicker(null)}>
      <div
        className="session-grid"
        style={{
          gridTemplateColumns: `110px repeat(${Math.max(channels.length, 1)}, minmax(84px, 1fr)) 44px`,
        }}
      >
        <div className="session-corner" />
        {channels.map((ch) => (
          <div
            key={ch.id}
            className="session-colhead"
            style={{ '--ch-color': ch.color } as CSSProperties}
          >
            {ch.name}
          </div>
        ))}
        <div className="session-corner scene-col" />

        {project.scenes.map((scene, idx) => (
          <Fragment key={scene.id}>
            <div className="session-scene-label">
              {renaming === scene.id ? (
                <input
                  className="scene-name-input"
                  autoFocus
                  defaultValue={scene.name}
                  onBlur={(e) => {
                    renameScene(scene.id, e.target.value.trim() || scene.name);
                    setRenaming(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className="scene-name"
                  onDoubleClick={() => setRenaming(scene.id)}
                  title="Double-click to rename"
                >
                  {scene.name}
                </span>
              )}
              <button className="scene-x" title="Remove scene" onClick={() => removeScene(scene.id)}>
                ×
              </button>
            </div>
            {channels.map((ch) => {
              const patternId = scene.cells[ch.id] ?? null;
              const pattern = patternId ? project.patterns[patternId] : null;
              const active = !!patternId && sessionActive[ch.id] === patternId;
              return (
                <button
                  key={ch.id}
                  className={`session-cell ${active ? 'active' : ''} ${patternId ? '' : 'empty'}`}
                  style={{ '--ch-color': ch.color } as CSSProperties}
                  onClick={() => {
                    if (patternId) void cellToggle(ch.id, patternId);
                  }}
                  onDoubleClick={() => setPicker({ sceneId: scene.id, channelId: ch.id })}
                  title={
                    pattern
                      ? `Launch ${pattern.name} — double-click to change`
                      : 'Double-click to assign a pattern'
                  }
                >
                  {pattern ? pattern.name : '—'}
                </button>
              );
            })}
            <button
              className={`session-scene-trigger ${sessionActiveScene === idx ? 'active' : ''}`}
              onClick={() => void sceneToggle(idx)}
              title={`Launch scene ${scene.name}`}
            >
              ▶
            </button>
          </Fragment>
        ))}
      </div>

      <div className="session-footer">
        <button className="pill-btn" onClick={() => addScene()}>
          + Scene
        </button>
        <span className="session-hint">
          Click a cell to launch/stop its clip · double-click to assign a pattern · ▶ launches the
          whole scene
        </span>
      </div>

      {picker && pickerScene && pickerChannel && (
        <div className="cell-picker" onPointerDown={(e) => e.stopPropagation()}>
          <div className="cell-picker-title">Pattern for {pickerChannel.name}</div>
          <button
            className={pickerScene.cells[pickerChannel.id] == null ? 'current' : ''}
            onClick={() => {
              setSceneCell(pickerScene.id, pickerChannel.id, null);
              setPicker(null);
            }}
          >
            — none —
          </button>
          {Object.values(project.patterns).map((pat) => (
            <button
              key={pat.id}
              className={pickerScene.cells[pickerChannel.id] === pat.id ? 'current' : ''}
              onClick={() => {
                setSceneCell(pickerScene.id, pickerChannel.id, pat.id);
                setPicker(null);
              }}
            >
              {pat.name}
            </button>
          ))}
          <button
            className="pill-btn"
            onClick={() => {
              const id = createPattern();
              setSceneCell(pickerScene.id, pickerChannel.id, id);
              setPicker(null);
            }}
          >
            + New pattern
          </button>
        </div>
      )}
    </div>
  );
}